import './goelett-quote.css'

import { useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { BookingDataForm } from './components/BookingDataForm'
import { BookingResult } from './components/BookingResult'
import { BookingSteps } from './components/BookingSteps'
import { DeadlineBadge } from './components/DeadlineBadge'
import { PinGate } from './components/PinGate'
import { QuoteHeader } from './components/QuoteHeader'
import { QuoteToolbar } from './components/QuoteToolbar'
import { SelectionSummary } from './components/SelectionSummary'
import { ServiceSection } from './components/ServiceSection'
import { StatusPanel } from './components/StatusPanel'
import { WaitingPanel } from './components/WaitingPanel'
import { useGoelettQuote } from './hooks/useGoelettQuote'
import { QuoteI18nProvider } from './i18n'
import { legTitle } from './legGroups'
import { useQuoteI18n } from './quoteI18n'

/**
 * Pagina preventivo.
 *
 * Il provider della lingua avvolge tutto — anche il caricamento dei dati, che usa i testi
 * tradotti per gli errori — e la lingua salvata sul preventivo entra in gioco appena il
 * record arriva (`adoptQuoteLanguage`). Così i componenti leggono `t` dal contesto senza
 * passarselo l'uno con l'altro.
 */
export function GoelettQuotePage() {
  const { token } = useParams()

  return (
    <QuoteI18nProvider>
      <QuoteScreen token={token} />
    </QuoteI18nProvider>
  )
}

function QuoteScreen({ token }) {
  const quote = useGoelettQuote(token)
  const { adoptQuoteLanguage } = useQuoteI18n()

  // La lingua del messaggio ricevuto dal viaggiatore è il default della pagina: si applica
  // appena il preventivo è stato letto, e cede sempre a una scelta manuale.
  useEffect(() => {
    adoptQuoteLanguage(quote.data?.language)
  }, [quote.data?.language, adoptQuoteLanguage])

  return <QuoteContent {...quote} />
}

function QuoteContent({
  data,
  phase,
  error,
  stalled,
  msLeft,
  verifyingPin,
  pinError,
  handleUnlock,
  selectionsByLeg,
  selections,
  legGroups,
  allLegsSelected,
  toggleOffer,
  submitting,
  bookingForm,
  handleBook,
  handleSubmitBookingData
}) {
  const { t } = useQuoteI18n()

  const handleToggleAndScroll = useCallback(
    (offer) => {
      const legKey = offer.legKey || offer.segmentType
      const wasSelected = selectionsByLeg[legKey]?.offerId === offer.offerId
      toggleOffer(offer)
      if (wasSelected) return

      requestAnimationFrame(() => {
        const currentIdx = legGroups.findIndex((g) => g.key === legKey)
        for (let i = currentIdx + 1; i < legGroups.length; i++) {
          const nextKey = legGroups[i].key
          if (!selectionsByLeg[nextKey]) {
            document
              .getElementById(`gq-section-${nextKey}`)
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            return
          }
        }
      })
    },
    [toggleOffer, selectionsByLeg, legGroups]
  )

  if (phase === 'loading') {
    return (
      <PageShell>
        <div className="gq-panel">
          <div className="gq-spinner" />
          <p className="gq-panel__text">{t.loading}</p>
        </div>
      </PageShell>
    )
  }

  if (phase === 'failed') {
    return (
      <PageShell>
        <QuoteHeader data={data} />
        <StatusPanel
          tone="danger"
          icon="⚠️"
          title={t.unavailable.title}
          text={error || t.unavailable.text}
          hint={t.unavailable.hint}
        />
      </PageShell>
    )
  }

  if (phase === 'expired') {
    return (
      <PageShell>
        <QuoteHeader data={data} />
        <StatusPanel
          tone="warning"
          icon="⏰"
          title={t.expired.title}
          text={t.expired.text}
          hint={t.expired.hint}
        />
      </PageShell>
    )
  }

  // Il preventivo non si apre col solo link: serve il codice recapitato al viaggiatore.
  if (phase === 'locked') {
    return (
      <PageShell>
        <QuoteHeader data={data} />
        {/* Il tempo corre già mentre si inserisce il codice: la finestra parte dalla
            ricerca, non dall'apertura della pagina. */}
        <DeadlineBadge msLeft={msLeft} />
        <PinGate
          attemptsLeft={data?.attemptsLeft ?? 0}
          verifying={verifyingPin}
          error={pinError}
          onSubmit={handleUnlock}
        />
      </PageShell>
    )
  }

  if (phase === 'done') {
    return (
      <PageShell>
        <QuoteHeader data={data} />
        <BookingSteps phase={phase} />
        <BookingResult
          tripCode={data?.tripCode}
          tripStatus={data?.tripStatus}
          cartLink={data?.cartLink}
        />
      </PageShell>
    )
  }

  return (
    <PageShell>
      <QuoteHeader data={data} />
      {/* Solo dove il viaggiatore deve ancora agire (scelta e form): in `preparing` e
          `finalizing` sta lavorando l'Executor e il conto alla rovescia non si applica. */}
      {(phase === 'choosing' || phase === 'form') && <DeadlineBadge msLeft={msLeft} />}
      <BookingSteps phase={phase} />

      {phase === 'choosing' && (
        <>
          {/* Una sezione per tratta, non per verticale: l'A/R in treno sono due scelte. */}
          {legGroups.map((group) => (
            <ServiceSection
              key={group.key}
              id={`gq-section-${group.key}`}
              serviceKey={group.serviceKey}
              title={legTitle(t, group)}
              offers={group.offers}
              selectedOffer={selectionsByLeg[group.key]}
              onToggle={handleToggleAndScroll}
            />
          ))}
          <SelectionSummary
            id="gq-cart"
            selections={selections}
            legGroups={legGroups}
            allLegsSelected={allLegsSelected}
            submitting={submitting}
            onBook={handleBook}
          />
          {error && (
            <p role="alert" className="gq-error" style={{ marginTop: 'var(--space-3)' }}>
              {error}
            </p>
          )}
          <p className="gq-footnote">{t.footnote}</p>
        </>
      )}

      {phase === 'preparing' && <WaitingPanel variant="preparing" stalled={stalled} />}

      {phase === 'form' &&
        (bookingForm ? (
          <BookingDataForm
            form={bookingForm}
            submitting={submitting}
            error={error}
            onSubmit={handleSubmitBookingData}
          />
        ) : (
          <WaitingPanel variant="preparing" stalled={stalled} />
        ))}

      {phase === 'finalizing' && <WaitingPanel variant="finalizing" stalled={stalled} />}
    </PageShell>
  )
}

function PageShell({ children }) {
  return (
    <div className="gq">
      <div className="gq__shell">
        <QuoteToolbar />
        {children}
      </div>
    </div>
  )
}
